<template>
  <div class="mindmap" @click="$emit('close')">
    <div class="wrap" @click.stop>
      <div class="leftbar">
        <div class="outline">
          <div class="outline-item title" key="title" @click="changeData('root')">{{ title }}</div>
          <div class="outline-item" :class="{ 'active': activeId === item.id }" v-for="item in outline" :key="item.id" @click="changeData(item.id)">{{ item.title }}</div>
        </div>
        <div class="jump-tips">点击思维导图节点，跳转到对应章节</div>
        <a-radio-group size="small" class="layout" v-model:value="layout" button-style="solid" @change="changeLayout()">
          <a-radio-button value="H">
            两侧分布
          </a-radio-button>
          <a-radio-button value="LR">
            向右分布
          </a-radio-button>
        </a-radio-group>
      </div>

      <div class="container" ref="containerRef"></div>
    </div>

    <div class="close-btn">
      <svg viewBox="0 0 1024 1024" fill="currentColor" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em">
        <path d="M578.36284173 512l422.30899284-422.30899284c18.09895683-18.09895683 18.09895683-48.2638849 0-66.36284173-18.09895683-18.09895683-48.2638849-18.09895683-66.36284173 0l-422.30899284 422.30899284-422.30899284-422.30899284c-18.09895683-18.09895683-48.2638849-18.09895683-66.36284173 0-18.09895683 18.09895683-18.09895683 48.2638849 0 66.36284173l422.30899284 422.30899284-422.30899284 422.30899284c-18.09895683 18.09895683-18.09895683 48.2638849 0 66.36284173 18.09895683 18.09895683 48.2638849 18.09895683 66.36284173 0l422.30899284-422.30899284 422.30899284 422.30899284c18.09895683 18.09895683 48.2638849 18.09895683 66.36284173 0 18.09895683-18.09895683 18.09895683-48.2638849 0-66.36284173l-422.30899284-422.30899284z"></path>
      </svg>
    </div>
  </div>
</template>

<script>
import G6 from '@antv/g6'

const fontSize = 16

function buildHierarchy(arr, pid) {
  const root = { level: 0, children: [] }
  const stack = [root]

  for(const item of arr) {
    const newItem = {
      level: item.level,
      id: item.id,
      label: item.content,
      pid,
      nodeType: 'heading',
    }
    if(item.isDetailHeading) newItem.isDetailHeading = item.isDetailHeading
    if(item.detailId) newItem.detailId = item.detailId

    while(stack.length > 1 && stack[stack.length - 1].level >= newItem.level) {
      stack.pop()
    }

    const parentNode = stack[stack.length - 1]
    if(!parentNode.children) {
      parentNode.children = []
    }
    parentNode.children.push(newItem)

    stack.push(newItem)
  }

  return root.children
}

const fittingString = (str, maxWidth, fontSize) => {
  const ellipsis = '...'
  const ellipsisLength = G6.Util.getTextSize(ellipsis, fontSize)[0]
  let currentWidth = 0
  let res = str
  const pattern = new RegExp('[\u4E00-\u9FA5]+')
  str.split('').forEach((letter, i) => {
    if(currentWidth > maxWidth - ellipsisLength) return
    if(pattern.test(letter)) {
      currentWidth += fontSize
    } 
    else {
      currentWidth += G6.Util.getLetterWidth(letter, fontSize)
    }
    if(currentWidth > maxWidth - ellipsisLength) {
      res = `${str.substr(0, i)}${ellipsis}`
    }
  })
  return res
}

G6.registerNode('crect-root', {
  jsx: cfg => {
    const width = G6.Util.getTextSize(cfg.label, fontSize)[0] + 24
    const stroke = cfg.style.stroke || '#e86b99'

    return `
      <group>
        <rect draggable="true" style={{
          width: ${width},
          height: 40,
          fill: ${cfg.hover ? '#f7ccdb' : '#fce9f0'},
          stroke: ${stroke},
          radius: 4,
          lineWidth: 2
        }} keyshape>
          <text style={{ fontSize: ${fontSize}, marginLeft: 12, marginTop: 14 }}>${cfg.label}</text>
        </rect>
      </group>
    `
  },
  getAnchorPoints() {
    return [
      [0, 0.5],
      [1, 0.5],
    ]
  },
}, 'single-node')

G6.registerNode('crect', {
  draw: (cfg, group) => {
    const width = cfg.label.length * 10
    const rect = group.addShape('rect', {
      attrs: {
        x: 0,
        y: -10,
        ...cfg.style,
        width,
        height: 20,
        lineWidth: 0,
        opacity: 0,
      },
      name: 'rect-shape',
      draggable: true,
    })
    const label = group.addShape('text', {
      attrs: {
        text: fittingString(cfg.label, 380, fontSize),
        fill: cfg.hover ? '#e86b99' : '#333',
        fontSize,
        x: 0,
        y: 0,
      },
      name: 'label-shape',
      draggable: true,
    })
    const bboxWidth = label.getBBox().width + 10
    rect.attr({ width: bboxWidth })

    group.addShape('path', {
      attrs: {
        lineWidth: 2,
        fill: '#e86b99',
        stroke: '#e86b99',
        path: [
          ['M', 0, 0],
          ['L', bboxWidth, 0],
        ],
      },
      name: 'path-shape',
      draggable: true,
    })

    return rect
  },
  getAnchorPoints: () => {
    return [
      [0, 0.5],
      [1, 0.5],
    ]
  },
})

G6.registerBehavior('dice-mindmap', {
  getEvents() {
    return {
      'node:mouseenter': 'hoverNode',
      'node:mouseleave': 'hoverNodeOut',
    }
  },
  hoverNode(evt) {
    evt.currentTarget.updateItem(evt.item, { hover: true })
  },
  hoverNodeOut(evt) {
    evt.currentTarget.updateItem(evt.item, { hover: false })
  },
})

const dataTransform = data => {
  return {
    ...data,
    type: 'crect-root'
  }
}

export default {
  name: 'mindmap',
  props: {
    outline: {
      type: Array,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    bookId: {
      type: String,
      required: true,
    },
    defaultActiveId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      graph: null,
      layout: 'H',
      graphData: null,
      activeId: 'root',
    }
  },
  mounted() {
    const children = this.outline.map(item => {
      if(item.children) {
        const children = buildHierarchy(item.children, item.id)
        return {
          id: item.id,
          label: item.title,
          nodeType: 'unit',
          children,
        }
      }
      return {
        id: item.id,
        label: item.title,
        nodeType: 'unit',
      }
    })
    const data = {
      id: 'root',
      label: this.title,
      children,
    }
    this.graphData = data

    const container = this.$refs.containerRef
    const width = container.clientWidth
    const height = container.clientHeight

    const graph = new G6.TreeGraph({
      container,
      width,
      height,
      animate: false,
      fitCenter: true,
      modes: {
        default: [
          'drag-canvas',
          'zoom-canvas',
          'dice-mindmap',
        ],
      },
      defaultNode: {
        type: 'crect',
      },
      defaultEdge: {
        type: 'cubic-horizontal',
        style: {
          lineWidth: 2,
          stroke: '#e86b99',
        }
      },
      layout: {
        type: 'mindmap',
        direction: this.layout,
        getId: d => d.id,
        getHeight: () => fontSize,
        getVGap: () => 12,
        getHGap: () => 90,
        getWidth: node => {
          let w = G6.Util.getTextSize(node.label, fontSize)[0]
          if(w > 380) w = 380
          return w
        },
      },
    })

    graph.on('node:click', evt => {
      const node = evt.item
      const { id, pid, nodeType, isDetailHeading, detailId } = node.getModel()
      
      if(id === 'root') return
      if(nodeType === 'heading') {
        this.$emit('recordJump', { type: 'mindmap', inductionId: this.activeId })
        const data = { inductionId: pid, id }
        if(isDetailHeading) {
          data.isDetailHeading = isDetailHeading
          data.detailId = detailId
        }
        this.$emit('scrollToOutline', data)
      }
      else if(nodeType === 'unit') {
        this.$emit('recordJump', { type: 'mindmap', inductionId: this.activeId })
        this.$emit('scrollToOutline', { inductionId: id, id: '' })
      }

      this.$emit('close')
    })

    graph.data(dataTransform(data))
    graph.render()
    graph.fitView()

    graph.get('canvas').set('localRefresh', false)

    this.graph = graph

    const activeId = this.defaultActiveId || this.bookId
    this.changeData(activeId)
  },
  methods: {
    changeLayout(fitView = true) {
      this.graph.changeLayout({
        type: 'mindmap',
        direction: this.layout,
        getHeight: () => fontSize,
        getVGap: () => 12,
        getHGap: () => 90,
        getWidth: node => {
          let w = G6.Util.getTextSize(node.label, fontSize)[0]
          if(w > 380) w = 380
          return w
        },
      })
      if(fitView) this.graph.fitView()
    },
    changeData(id) {
      if(id === 'root') {
        this.graph.changeData(dataTransform(this.graphData))
        this.layout = 'H'
      }
      else {
        const data = this.graphData.children.find(item => item.id === id)
        this.graph.changeData(dataTransform(data))
        this.layout = 'LR'
      }
      this.activeId = id
      this.changeLayout(false)
      this.graph.fitView()
    },
  },
}
</script>

<style lang="scss" scoped>
.mindmap {
  position: fixed;
  inset: 0;
  z-index: 999;
  background-color: rgba($color: #000, $alpha: .15);
}
.wrap {
  position: absolute;
  inset: 40px;
  border-radius: 12px;
  box-shadow: 0 0 30px #e3e3e3;
  background-color: #fff;
  display: flex;
}
.leftbar {
  width: 240px;
  height: 100%;
  flex-shrink: 0;
  overflow: auto;
  border-right: 1px solid #eaeaea;
  display: flex;
  flex-direction: column;
  padding: 15px 12px;

  .outline {
    flex: 1;
    overflow: auto;
    font-size: 13px;
    line-height: 1.5;

    .outline-item {
      padding: 5px;
      border-radius: 4px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      cursor: pointer;

      &.title {
        font-size: 16px;
        font-weight: 700;
      }

      & + .outline-item {
        margin-top: 5px;
      }

      &.active {
        background-color: rgba($color: #e86b99, $alpha: .15);
      }
      &:hover {
        color: #e86b99;
      }
    }
  }
  .jump-tips {
    height: 28px;
    font-size: 12px;
    color: #888;
    flex-shrink: 0;
    display: flex;
    align-items: flex-end;
    border-top: 1px solid #eaeaea;
  }
  .layout {
    height: 40px;
    flex-shrink: 0;
    display: flex;
    align-items: flex-end;
  }
}
.container {
  flex: 1;
}

.close-btn {
  width: 80px;
  height: 80px;
  background-color: rgba(0, 0, 0, .6);
  border-radius: 50%;
  position: fixed;
  right: -40px;
  top: -40px;
  z-index: 999;
  cursor: pointer;

  svg {
    position: absolute;
    bottom: 18px;
    left: 18px;
    font-size: 13px;
    color: #fff;
  }
}
</style>